import {
  NodeStatusEvent,
  StatusUpdateEvent,
  TerminationEvent,
  NodeEvent,
  WorkflowEventTypes,
  NodeEventType,
  NodeDataType,
  LifecycleEvent,
} from '@/types/workflow-types';
import { accumulateStreamingText } from '@/utils/accumulate-streaming-text';

/** Type guard: checks if event is a NodeEvent (has node_id, node_display_name, display_type) */
export const isNodeEvent = (event: NodeStatusEvent): event is NodeEvent => {
  return (
    'node_id' in event &&
    'node_display_name' in event &&
    'display_type' in event
  );
};

/** Checks if a node display name indicates an agent-type or slides-type node */
const isAgentNodeName = (displayName: string | undefined): boolean => {
  if (!displayName) return false;
  const lowerName = displayName.toLowerCase();
  return lowerName.includes('agent') || lowerName.includes('slides');
};

/** Set of display types that should use streaming accumulation */
const STREAMING_DISPLAY_TYPES = new Set<NodeDataType>([
  NodeDataType.AGENT_MESSAGE,
  NodeDataType.CHAT_AGENT_MESSAGE,
  NodeDataType.THINKING,
  NodeDataType.TOOL_CALL,
  NodeDataType.TOOL_RESULT,
]);

/** Checks if a node event should participate in streaming accumulation */
const shouldStreamEvent = (nodeEvent: NodeEvent): boolean => {
  const isThinking = nodeEvent.display_type === NodeDataType.THINKING;
  const isAgentWithStreamType =
    isAgentNodeName(nodeEvent.node_display_name) &&
    STREAMING_DISPLAY_TYPES.has(nodeEvent.display_type);

  return isThinking || isAgentWithStreamType;
};

/** Checks if a display type represents a streamable event (for stable key generation) */
const isStreamableDisplayType = (displayType: NodeDataType): boolean => {
  return STREAMING_DISPLAY_TYPES.has(displayType);
};

/**
 * Processes a sorted list of events and accumulates streaming text chunks into
 * single consolidated events, preserving the original event order.
 */
export const processStreamingEvents = (
  sortedEvents: NodeStatusEvent[],
): NodeStatusEvent[] => {
  const processedEvents: NodeStatusEvent[] = [];
  const streamingAccumulator = new Map<string, string>();
  const streamingBlocks = new Map<string, { event: NodeEvent }>();
  const completedStreamingBlocks = new Set<string>();

  // First pass: identify streaming blocks and accumulate content
  sortedEvents.forEach((event) => {
    if (!isNodeEvent(event)) return;

    const streamKey = event.block_id || event.node_id;

    // Mark completion events
    if (
      event.event_type === NodeEventType.EXECUTION_SUCCESSFUL ||
      event.event_type === NodeEventType.EXECUTION_FAILED ||
      event.event_type === NodeEventType.END
    ) {
      if (event.block_id) {
        completedStreamingBlocks.add(event.block_id);
      }
      const streamingBlock = streamingBlocks.get(streamKey);
      if (streamingBlock) {
        streamingBlock.event = {
          ...streamingBlock.event,
          event_type: event.event_type,
        };
      } else {
        // Fallback: completion event may have a different block_id than the
        // streaming PROCESSING events. Match all streaming blocks that belong
        // to the same node_id so their spinners stop on completion.
        streamingBlocks.forEach((block) => {
          if (block.event.node_id === event.node_id) {
            block.event = {
              ...block.event,
              event_type: event.event_type,
            };
          }
        });
      }
    }

    // Check if this should be accumulated as a streaming event
    if (
      shouldStreamEvent(event) &&
      event.mode === 'textstream' &&
      !(event.block_id && completedStreamingBlocks.has(event.block_id))
    ) {
      if (!streamingBlocks.has(streamKey)) {
        streamingBlocks.set(streamKey, { event });
      }

      const currentContent = streamingAccumulator.get(streamKey) || '';
      const newData = typeof event.data === 'string' ? event.data : '';
      const accumulatedContent = accumulateStreamingText(
        currentContent,
        newData,
      );

      streamingAccumulator.set(streamKey, accumulatedContent);

      const block = streamingBlocks.get(streamKey);
      if (block) {
        block.event = { ...event, data: accumulatedContent };
      }
    }
  });

  // Mark superseded streaming blocks as complete: if a node has produced
  // events after the last event in a streaming block (e.g. thinking finished,
  // then tool calls started), the block is done even without a block-level
  // END event from the backend. Uses sorted indices instead of timestamp
  // comparison to handle equal timestamps and timezone offset differences.
  const lastNodeEventIndex = new Map<string, number>();
  const streamBlockLastIndex = new Map<string, number>();
  sortedEvents.forEach((event, index) => {
    if (!isNodeEvent(event)) return;
    lastNodeEventIndex.set(event.node_id, index);
    const sk = event.block_id || event.node_id;
    if (streamingBlocks.has(sk)) {
      streamBlockLastIndex.set(sk, index);
    }
  });
  streamingBlocks.forEach((block, key) => {
    if (block.event.event_type !== NodeEventType.PROCESSING) return;
    const blockLastIdx = streamBlockLastIndex.get(key);
    const nodeLastIdx = lastNodeEventIndex.get(block.event.node_id);
    if (
      blockLastIdx !== undefined &&
      nodeLastIdx !== undefined &&
      nodeLastIdx > blockLastIdx
    ) {
      block.event = { ...block.event, event_type: NodeEventType.END };
    }
  });

  // Second pass: build final event list preserving original order
  const streamingBlocksUsed = new Set<string>();

  sortedEvents.forEach((event) => {
    if (isNodeEvent(event)) {
      const streamKey = event.block_id || event.node_id;

      if (streamingBlocks.has(streamKey)) {
        if (!streamingBlocksUsed.has(streamKey)) {
          streamingBlocksUsed.add(streamKey);
          const block = streamingBlocks.get(streamKey);
          if (block) {
            processedEvents.push(block.event);
          }
        }
        return;
      }
    }

    processedEvents.push(event);
  });

  return processedEvents;
};

/** Generates a stable React key for an event item */
export const getStableEventKey = (
  eventKey: string,
  event: NodeStatusEvent,
  index?: number,
): string => {
  if (isNodeEvent(event)) {
    if (
      event.mode === 'textstream' &&
      isStreamableDisplayType(event.display_type)
    ) {
      const streamId = event.block_id || event.node_id;
      return `${eventKey}-${streamId}-stream`;
    }
  }
  // Fallback for events without event_id (e.g., some workflow-level events from the backend)
  const id =
    event.event_id ?? `${event.event_type}-${event.timestamp}-${index ?? 0}`;
  return `${eventKey}-${id}`;
};

export type EventCategory =
  | 'thinking'
  | 'toolCalls'
  | 'toolResults'
  | 'responses'
  | 'results'
  | 'errors'
  | 'retries'
  | 'chat'
  | 'status';

export const EVENT_CATEGORY_KEYS: EventCategory[] = [
  'thinking',
  'toolCalls',
  'toolResults',
  'responses',
  'results',
  'errors',
  'retries',
  'chat',
  'status',
];

export const EVENT_CATEGORY_LABELS: Record<EventCategory, string> = {
  thinking: 'Chain of thought',
  toolCalls: 'Tool calls',
  toolResults: 'Tool results',
  responses: 'Agent responses',
  results: 'Results',
  errors: 'Errors',
  retries: 'Retries',
  chat: 'Chat & input',
  status: 'Status & lifecycle',
};

export type EventFilterPreset = 'all' | 'cot' | 'results' | 'errors' | 'tools';

export const EVENT_FILTER_PRESETS: {
  key: EventFilterPreset;
  label: string;
  categories: EventCategory[];
}[] = [
  { key: 'all', label: 'All events', categories: [...EVENT_CATEGORY_KEYS] },
  {
    key: 'cot',
    label: 'Chain of thought',
    categories: ['thinking', 'responses', 'results'],
  },
  {
    key: 'results',
    label: 'Results only',
    categories: ['results', 'toolResults'],
  },
  {
    key: 'errors',
    label: 'Errors & retries',
    categories: ['errors', 'retries'],
  },
  { key: 'tools', label: 'Tools', categories: ['toolCalls', 'toolResults'] },
];

/** Maps an event to the filterable category it belongs to. */
export const getEventCategory = (event: NodeStatusEvent): EventCategory => {
  if (!isNodeEvent(event)) {
    return 'status';
  }
  if (event.event_type === NodeEventType.AWAITING_USER_INPUT) {
    return 'chat';
  }
  if (
    event.event_type === NodeEventType.EXECUTION_FAILED ||
    event.display_type === NodeDataType.ERROR
  ) {
    return 'errors';
  }
  if (event.event_type === NodeEventType.RETRY_SCHEDULED) {
    return 'retries';
  }
  switch (event.display_type) {
    case NodeDataType.THINKING:
      return 'thinking';
    case NodeDataType.TOOL_CALL:
      return 'toolCalls';
    case NodeDataType.TOOL_RESULT:
      return 'toolResults';
    case NodeDataType.AGENT_MESSAGE:
    case NodeDataType.CHAT_AGENT_MESSAGE:
      return 'responses';
    case NodeDataType.NODE_RESULT:
      return 'results';
    case NodeDataType.CHAT_REQUEST:
    case NodeDataType.CHAT_RESPONSE:
    case NodeDataType.CHAT_EVENT:
      return 'chat';
    default:
      break;
  }
  if (event.event_type === NodeEventType.EXECUTION_SUCCESSFUL) {
    return 'results';
  }
  return 'status';
};

/** Extracts the message string from any event type */
export const getEventMessage = (event: NodeStatusEvent): string => {
  if (event.event_type === WorkflowEventTypes.STATUS_UPDATE) {
    return (event as StatusUpdateEvent).message;
  }
  if (event.event_type === WorkflowEventTypes.TERMINATION) {
    return `Workflow terminated with status code: ${(event as TerminationEvent).status_code}`;
  }
  if (event.event_type === WorkflowEventTypes.EXECUTION_RESULT) {
    return (event as import('@/types/workflow-types').ExecutionResultEvent)
      .message;
  }
  if (event.event_type === WorkflowEventTypes.LIFECYCLE) {
    // Lifecycle events carry a human-readable ``message`` string the
    // BE composes per phase (e.g. "Workflow paused",
    // "Edit applied; 3 cached result(s) invalidated").  Returning it
    // here lets the events panel render the actual context instead
    // of an empty body.
    return (event as LifecycleEvent).message;
  }
  if (isNodeEvent(event)) {
    return event.dynamic_message;
  }
  return '';
};
